import * as z from "zod";

//#region src/domain/document.constants.ts
/**
* File types a book document can have in Readiant.
*/
const DOCUMENT_TYPES = ["PDF", "ePub"];
/**
* Minimum length of the document id prefix a unique code is built from.
*/
const UNIQUE_CODE_PREFIX_MIN_LENGTH = 3;
/**
* Number of Base62 characters a unique code spends on its expiry date.
*/
const UNIQUE_CODE_TTL_CHARACTERS = 2;
/**
* Minimum total length of a unique code: the shortest document id prefix plus
* its expiry characters.
*/
const UNIQUE_CODE_MIN_LENGTH = 5;
/**
* Maximum total length of a unique code: Readiant document ids are 20
* characters, so a code never exceeds the full id plus its expiry characters.
*/
const UNIQUE_CODE_MAX_LENGTH = 22;

//#endregion
//#region src/domain/document.schemas.ts
/**
* @public
*/
const DocumentTypeSchema = z.enum(DOCUMENT_TYPES).meta({
	title: "DocumentType",
	description: "File type of the document in Readiant.",
	examples: ["PDF"]
});
/**
* Canonical example, shared by {@link BookDocumentSchema}'s meta and the
* contract's response schema examples.
*
* @public
*/
const BookDocumentExample = {
	id: "05RlueqwxaR5srvOyO3Q",
	added: "1663674828532",
	title: "Sophie op de daken",
	lesmethode: "blinklezen",
	type: "PDF",
	pages: 216
};
/**
* A document Readiant left unfiled: it omits the keys it has no value for
* rather than sending them empty.
*
* @public
*/
const UnfiledBookDocumentExample = {
	id: "066lZmnWHpn3iwIHJ23k",
	title: "Filmles Het paard van Sinterklaas",
	type: "PDF",
	pages: 3
};
/**
* A book document as Readiant describes it.
*
* `added` and `lesmethode` are absent on documents Readiant has no import date
* or method for, so both are optional.
*
* @public
*/
const BookDocumentSchema = z.object({
	id: z.string(),
	added: z.xor([z.number(), z.string()]).optional(),
	title: z.string(),
	lesmethode: z.string().optional(),
	type: DocumentTypeSchema,
	pages: z.number().int().nonnegative()
}).meta({
	title: "BookDocument",
	description: "A book document available in Readiant.",
	examples: [BookDocumentExample, UnfiledBookDocumentExample]
});
/**
* The code in a short URL or embed code: a unique document id prefix followed
* by two Base62 characters encoding the expiry date.
*
* @public
*/
const UniqueCodeSchema = z.string().trim().min(5).max(22).regex(/^[\dA-Za-z]+$/, "must be alphanumeric").meta({
	title: "UniqueCode",
	description: "Document id prefix followed by two Base62 expiry characters, as used in short URLs.",
	examples: ["05RcS"]
});
/**
* @public
*/
const CreateShortUrlDtoSchema = z.object({
	documentId: z.string().optional(),
	/**
	* @deprecated Use `documentId`.
	*/
	readiantId: z.string().optional()
}).meta({
	title: "CreateShortUrlDto",
	description: "Document to create a short URL for. `readiantId` is the deprecated alias of `documentId`.",
	examples: [{ documentId: "05RlueqwxaR5srvOyO3Q" }]
});
/**
* @public
*/
const ShortUrlSchema = z.object({
	code: UniqueCodeSchema,
	url: z.url(),
	/**
	* @deprecated Echoes the request's `readiantId`; use `code` and `url`.
	*/
	readiantId: z.string().optional()
}).meta({
	title: "ShortUrl",
	description: "Short URL pointing at the reader for a single document.",
	examples: [{
		code: "05RcS",
		url: "https://reader.blink.nl/05RcS",
		readiantId: "05RlueqwxaR5srvOyO3Q"
	}]
});

//#endregion
export { ShortUrlSchema as a, DOCUMENT_TYPES as c, UNIQUE_CODE_PREFIX_MIN_LENGTH as d, UNIQUE_CODE_TTL_CHARACTERS as f, DocumentTypeSchema as i, UNIQUE_CODE_MAX_LENGTH as l, BookDocumentSchema as n, UnfiledBookDocumentExample as o, CreateShortUrlDtoSchema as r, UniqueCodeSchema as s, BookDocumentExample as t, UNIQUE_CODE_MIN_LENGTH as u };